import { Chip } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

export const getColor = (status) => {
  if (status === "rejected") {
    return "error";
  } else if (status === "approved") {
    return "success";
  } else if (status === "pending") {
    return "info";
  } else if (status === "queued") {
    return "warning";
  } else if (status === "complete") {
    return "success";
  } else if (status === "error") {
    return "error";
  } else if (status === "Abnormal Request") {
    return "error";
  } else if (status === "not-required") {
    return "warning";
  } else {
    return "secondary";
  }
};

const getStatusStyles = (status) => {
  const baseClasses =
    "px-2 py-0.5 rounded-full !text-[10px] font-bold uppercase tracking-wider inline-block";

  switch (status?.toLowerCase()) {
    case "rejected":
    case "error":
    case "abnormal request":
      return `${baseClasses} bg-red-100 !text-red-600 border border-red-200`;
    case "approved":
    case "complete":
    case "success":
      return `${baseClasses} bg-green-100 !text-green-600 border border-green-200`;
    case "pending":
      return `${baseClasses} bg-blue-100 !text-blue-600 border border-blue-200`;
    case "queued":
    case "not-required":
      return `${baseClasses} bg-yellow-100 !text-yellow-700 border border-yellow-200`;
    default:
      return `${baseClasses} bg-gray-100 !text-gray-600 border border-gray-200`;
  }
};

function ClaimResponseDetailComponent({ item }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  function formatDateTime(data) {
    if (!data) return "___";
    const date = new Date(data);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }

  const fields = [
    // Line 1
    { label: "Beneficiary Name", value: item?.patient_name },
    { label: "Document", value: item?.patient_document_id },
    { label: "Pre Auth Ref. No.", value: item?.pre_auth_ref_no },
    { label: "Is Referral", value: item?.is_referral, type: "switch" },
    { label: "Is Follow Up", value: item?.is_followup_req, type: "switch" },
    { label: "Is New Born", value: item?.is_new_born, type: "switch" },
    {
      label: "Transaction Date",
      value: item?.transaction_date
        ? formatDateTime(item?.transaction_date)
        : null,
    },

    // Line 2
    {
      label: "Date Ordered",
      value: item?.date_ordered ? formatDateTime(item?.date_ordered) : null,
    },
    { label: "Type", value: item?.type },
    { label: "Sub Type", value: item?.subtype },
    { label: "Outcome", value: item?.outcome, type: "chip" },
    { label: "Status", value: item?.status, type: "chip" },
    { label: "Disposition", value: item?.disposition },
    { label: "Cancellation Status", value: item?.calcellation_status },

    // Line 3 (was Line 4)
    { label: "Pre Auth Res. Url", value: item?.pre_auth_ref_url },
    {
      label: "Related Pre Auth Ref. No.",
      value: item?.related_pre_auth_ref_no,
    },
    { label: "Offline Eligibility Id", value: item?.offline_verification_id },
    {
      label: "Offline Eligibility Date",
      value: item?.offline_verification_date,
    },
    { label: "Ref Transaction ID", value: item?.followup_ref_transaction_id },

    // Line 4 (was Line 3)
    {
      label: "Eligibility Response Identifier (Value)",
      value: item?.verification_resp_ident_value,
    },
    {
      label: "Eligibility Response Identifier (Url)",
      value: item?.verification_resp_ident_url,
    },
    {
      label: "Related Pre Auth Request Identifier",
      value: item?.related_pre_auth_req_identifier,
    },
    {
      label: "Cancellation Status Reason",
      value: item?.calcellation_status_reason,
    },

    // Line 5
    { label: "TPA Name", value: item?.tpa_name },
    { label: "Pre Auth Res. Identifier", value: item?.pre_auth_ref_value },
    { label: "Pre Auth Period", value: item?.pre_auth_period },
    { label: "Insurer", value: item?.payer_name },
  ];

  const rows = [
    { data: fields.slice(0, 7), cols: "lg:grid-cols-5 xl:grid-cols-7" },
    { data: fields.slice(7, 14), cols: "lg:grid-cols-5 xl:grid-cols-7" },
    { data: fields.slice(14, 19), cols: "lg:grid-cols-5 xl:grid-cols-5" }, // Swapped Line 3 (5 items)
    { data: fields.slice(19, 23), cols: "lg:grid-cols-4 xl:grid-cols-4" }, // Swapped Line 4 (4 items)
    { data: fields.slice(23, 27), cols: "lg:grid-cols-4 xl:grid-cols-4" },
  ];

  return (
    <div className="eligibility_details_section border-slate-50 border rounded-lg overflow-hidden" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header matching the style requested (light gray bg) */}
      <div className="bg-gray-100 flex justify-between items-center px-2 py-1 border-b border-gray-200">
        <div className="flex flex-col">
          <span className="font-bold text-[14px] text-gray-700 uppercase tracking-wide">
            {t("Claim Response")}
          </span>
        </div>
        {/* {item?.status && (
          <div className="flex items-center">
            <span className={getStatusStyles(item.status)}>
              {t(item.status)}
            </span>
          </div>
        )} */}
      </div>

      <div className="flex flex-col gap-y-3 p-2 bg-white">
        {rows.map((row, rIdx) => (
          <div
            key={rIdx}
            className={`grid grid-cols-2 md:grid-cols-3 ${row.cols} gap-x-2 gap-y-2`}
          >
            {row.data.map((field, idx) => (
              <div key={idx} className="eligibility_details_user_data">
                {/* Label */}
                <div
                  className={`data_head !mb-0.5 ${
                    isRTL ? "!text-right" : "!text-left"
                  }`}
                >
                  <span className="!text-gray-500 !text-[11px] font-semibold uppercase tracking-tight">
                    {t(field.label)}
                  </span>
                </div>

                {/* Value */}
                <div
                  className={`data_cont mt-0.5 leading-tight ${
                    isRTL ? "!text-right" : "!text-left"
                  }`}
                >
                  {field.type === "switch" ? (
                    <label className="switch transform scale-[0.6] origin-left -ml-1 inline-block">
                      <input
                        type="checkbox"
                        checked={field.value || false}
                        disabled
                      />
                      <span className="slider"></span>
                    </label>
                  ) : field.type === "chip" ? (
                    <div className="mt-0.5 flex items-center h-[20px]">
                      {field.value ? (
                        <span className={getStatusStyles(field.value)}>
                          {t(field.value)}
                        </span>
                      ) : (
                        <span className="!text-gray-400 !text-[11px]">___</span>
                      )}
                    </div>
                  ) : (
                    <span
                      className={`!text-[11px] !font-bold break-words ${
                        field.label === "Site Eligibility"
                          ? "!text-green-700"
                          : "!text-[#0070C2]"
                      }`}
                    >
                      {field.value || (
                        <span className="!text-gray-400">___</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ClaimResponseDetailComponent;
